import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  Stack,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import useNotification from "../../hooks/useNotification";
import branch from "../../services/branch";
import opname from "../../services/opname";
import queryDefault from "../../utils/queryDefault";

interface FilterState {
  branches: any[];
  opnames: any[];
  status: string[];
  startDate: string;
  endDate: string;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("id-ID", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const SummaryCard = ({ title, value, color = "#1976d2" }: any) => (
  <Card>
    <CardContent>
      <Typography color="textSecondary" gutterBottom sx={{ fontSize: "0.875rem" }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ color, fontWeight: 700 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

export default function OpnameDashboardSection() {
  const notification = useNotification();
  const [filters, setFilters] = useState<FilterState>({
    branches: [],
    opnames: [],
    status: [],
    startDate: "",
    endDate: "",
  });

  const { data: branchesData } = useQuery(
    "dashboard-branches",
    () => branch.getAllBranch({}),
    queryDefault
  );

  const { data: opnamesData } = useQuery(
    "dashboard-opnames",
    () => opname.getOpnameAll(),
    queryDefault
  );

  const { data: opnameListData, isLoading } = useQuery(
    ["opname-dashboard", filters],
    () => {
      const params: any = {};
      if (filters.branches.length > 0) {
        params.branch_ids = filters.branches.map((b: any) => b.branch_id).join(",");
      }
      if (filters.opnames.length > 0) {
        params.opname_ids = filters.opnames.map((o: any) => o.id).join(",");
      }
      if (filters.status.length > 0) {
        params.status = filters.status.join(",");
      }
      if (filters.startDate && filters.endDate) {
        params.start_date = filters.startDate;
        params.end_date = filters.endDate;
      }
      return opname.getOpnameList(params);
    },
    {
      ...queryDefault,
      onError: (error: any) => {
        notification.onOpen({
          message: error?.response?.data?.message || "Failed to fetch dashboard data",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const summaryData = useMemo(() => {
    const items = opnameListData?.data || [];

    const totalNilaiStock = items.reduce(
      (sum: number, item: any) => sum + (item.ending_stock * item.price || 0),
      0
    );
    const totalMinus = items.reduce(
      (sum: number, item: any) => sum + (item.qty_minus || 0),
      0
    );
    const estimasiKerugian = items.reduce(
      (sum: number, item: any) => sum + (item.qty_minus * item.price || 0),
      0
    );

    return {
      totalNilaiStock,
      totalMinus,
      estimasiKerugian,
      transaksiMasuk: items.reduce((sum: number, item: any) => sum + (item.qty_masuk || 0), 0),
      transaksiKeluar: items.reduce((sum: number, item: any) => sum + (item.qty_keluar || 0), 0),
    };
  }, [opnameListData]);

  const chartData = useMemo(() => {
    const items = opnameListData?.data || [];
    if (items.length === 0)
      return { labels: [], datasets: [] };

    return {
      labels: items.map((item: any) => item.title),
      datasets: [
        {
          label: "Stock Awal",
          data: items.map((item: any) => item.qty_awal),
          backgroundColor: "#8884d8",
        },
        {
          label: "Ending Sistem",
          data: items.map((item: any) => item.ending_stock),
          backgroundColor: "#82ca9d",
        },
        {
          label: "Fisik (Masuk)",
          data: items.map((item: any) => item.qty_masuk),
          backgroundColor: "#ffc658",
        },
      ],
    };
  }, [opnameListData]);

  const breakdownItems = useMemo(() => {
    return (opnameListData?.data || []).map((item: any) => ({
      ...item,
      nilai_stock: item.ending_stock * item.price,
    }));
  }, [opnameListData]);

  const topMinusItems = useMemo(() => {
    return breakdownItems
      .sort((a: any, b: any) => b.qty_minus - a.qty_minus)
      .slice(0, 10);
  }, [breakdownItems]);

  const branchOptions = branchesData?.data || [];
  const opnameOptions = opnamesData?.data || [];

  if (isLoading) return <CircularProgress />;

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              options={branchOptions}
              getOptionLabel={(option: any) => option.branch_name}
              value={filters.branches}
              onChange={(e, value) => setFilters({ ...filters, branches: value })}
              renderInput={(params) => <TextField {...params} label="Branches" size="small" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              options={opnameOptions}
              getOptionLabel={(option: any) => option.title}
              value={filters.opnames}
              onChange={(e, value) => setFilters({ ...filters, opnames: value })}
              renderInput={(params) => <TextField {...params} label="Opnames" size="small" />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              type="date"
              label="Start Date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              type="date"
              label="End Date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard title="Total Nilai Stock" value={formatCurrency(summaryData.totalNilaiStock)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard title="Total Minus" value={summaryData.totalMinus} color="#d32f2f" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <SummaryCard
            title="Estimasi Kerugian Minus"
            value={formatCurrency(summaryData.estimasiKerugian)}
            color="#f57c00"
          />
        </Grid>
      </Grid>

      {/* Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Stock Awal vs Ending vs Fisik
          </Typography>
          {chartData.labels.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData.labels.map((label: any, idx: number) => ({
                name: label,
                "Stock Awal": chartData.datasets[0].data[idx],
                "Ending Sistem": chartData.datasets[1].data[idx],
                "Fisik (Masuk)": chartData.datasets[2].data[idx],
              }))}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Stock Awal" fill="#8884d8" />
                <Bar dataKey="Ending Sistem" fill="#82ca9d" />
                <Bar dataKey="Fisik (Masuk)" fill="#ffc658" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography color="textSecondary">No data to display</Typography>
          )}
        </CardContent>
      </Card>

      {/* Breakdown Table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Breakdown per Opname
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Masuk
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Keluar
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Minus
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Nilai Stock
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {breakdownItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 2, color: "text.secondary" }}>
                      No data
                    </TableCell>
                  </TableRow>
                ) : (
                  breakdownItems.map((item: any) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.title}</TableCell>
                      <TableCell align="right">{item.qty_masuk}</TableCell>
                      <TableCell align="right">{item.qty_keluar}</TableCell>
                      <TableCell align="right" sx={{ color: "#d32f2f" }}>
                        {item.qty_minus}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(item.nilai_stock)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Top Minus Items */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Selisih Terbesar
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Opname</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Minus
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topMinusItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 2, color: "text.secondary" }}>
                      No data
                    </TableCell>
                  </TableRow>
                ) : (
                  topMinusItems.map((item: any) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{item.branch_name}</TableCell>
                      <TableCell>{item.title}</TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>{item.type}</TableCell>
                      <TableCell align="right" sx={{ color: "#d32f2f", fontWeight: 600 }}>
                        {item.qty_minus}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
